import { Emitter } from '@patsnap/tahiti-core';
import type { Event } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { SpecificationDataService } from '../specification-data-service';
import type { SpecificationFigureService } from '../specification-figure-service';

export type AnnotationStyleType = 'line' | 'polyline' | 'curve';
export type AnnotationLineType = 'solid' | 'dashed' | 'dotted';

export interface AnnotationStyleParams {
    type?: AnnotationStyleType;
    lineType?: AnnotationLineType;
    lineWidth?: number;
    fontFamily?: string;
    fontSize?: number;
}

export type StorageData = {
    figures: {
        annotations: {
            termId: string;
        }[];
    }[];
    lastModified: number;
};

export type SpecificationData = {
    terms: Term[];
    figures: Figure[];
    lastModified: number;
};

export interface StorageProvider {
    save(data: StorageData, path: string): Promise<void>;
    load(path: string): Promise<StorageData>;
    exists(path: string): Promise<boolean>;
    delete(path: string): Promise<void>;
}

export interface Serializer<T> {
    serialize(entity: T): any;
    deserialize(data: any): T;
}

export interface PatentDataSerializers {
    term: Serializer<Term>;
    figure: Serializer<Figure>;
    annotation: Serializer<Annotation>;
}

export interface AnnotationParams {
    id: string;
    termId: string;
    figureId: string;
}

export interface FigureParams {
    id: string;
    order?: number;
    url?: string;
    title?: string;
    scale?: number;
    rotation?: number;
    x?: number;
    y?: number;
    isAbstract?: boolean;
}

export class AnnotationStyle {
    private _type: AnnotationStyleType;
    private _lineType: AnnotationLineType;
    private _lineWidth: number;
    private _fontFamily: string;
    private _fontSize: number;

    constructor(
        params: AnnotationStyleParams = {},
    ) {
        this._type = params.type ?? 'line';
        this._lineType = params.lineType ?? 'solid';
        this._lineWidth = params.lineWidth ?? 1;
        this._fontFamily = params.fontFamily ?? 'Helvetica';
        this._fontSize = params.fontSize ?? 12;
    }

    // Getter and Setter methods
    getType(): string { return this._type; }
    setType(type: AnnotationStyleType): void { this._type = type; }

    getLineType(): string { return this._lineType; }
    setLineType(lineType: AnnotationLineType): void { this._lineType = lineType; }

    getLineWidth(): number { return this._lineWidth; }
    setLineWidth(width: number): void { this._lineWidth = width; }

    getFontFamily(): string { return this._fontFamily; }
    setFontFamily(font: string): void { this._fontFamily = font; }

    getFontSize(): number { return this._fontSize; }
    setFontSize(size: number): void { this._fontSize = size; }
}

export class Annotation {
    private _id: string;
    private _number: string = '';
    private _termId: string;
    private _figureId: string;
    // @ts-ignore
    private _type: string;
    // @ts-ignore
    private _attrs: Record<string, unknown> = {};

    constructor(params: AnnotationParams) {
        const { id, termId, figureId } = params;
        this._id = id;
        this._termId = termId;
        this._figureId = figureId;
    }

    set type(value: string) {
        this._type = value;
    }

    get type() {
        return this._type;
    }

    set attrs(value: Record<string, unknown>) {
        this._attrs = value;
    }

    get attrs() {
        return this._attrs;
    }

    // Getter and Setter methods
    getId(): string { return this._id; }

    get id(): string { return this._id; }

    getNumber(): string { return this._number; }
    setNumber(number: string): void {
        this._number = number;
        this._attrs.text = number;
    }

    getTermId(): string { return this._termId; }
    getFigureId(): string { return this._figureId; }
}

export class DefaultAnnotationSerializer implements Serializer<Annotation> {
    serialize(annotation: Annotation): any {
        return {
            id: annotation.getId(),
            termId: annotation.getTermId(),
            figureId: annotation.getFigureId(),
            attrs: annotation.attrs,
            type: annotation.type,
        };
    }

    deserialize(data: any): Annotation {
        const annotation = new Annotation(
            {
                id: data.id,
                termId: data.termId,
                figureId: data.figureId,
            },
        );
        annotation.attrs = data.attrs;
        annotation.type = data.type;

        return annotation;
    }
}

export class Term {
    private _id: string;
    private _number: string = '';
    private _text: string;
    private _annotations: Annotation[];
    private _annotated: boolean = false;

    constructor(params: { id: string; text?: string; number?: string }) {
        this._id = params.id;
        this._text = params.text ?? '';
        this._annotations = [];
        if (params.number) {
            this._number = params.number;
        }
    }

    // Getter and Setter methods
    getId(): string { return this._id; }

    get id(): string { return this._id; }

    setId(id: string): void { this._id = id; }

    setText(text: string): void {
        this._text = text;
    }

    get text(): string { return this._text; }

    getNumber(): string { return this._number; }
    setNumber(number: string): void {
        if (this._number === number) return;
        this._number = number;
        // 更新所有关联标注的编号
        this._annotations.forEach(annotation => {
            annotation.setNumber(number);
        });
    }

    get number(): string { return this._number; }

    get annotations(): Annotation[] { return this._annotations; }

    addAnnotation(annotation: Annotation): void {
        if (!this._annotations.some(a => a.getId() === annotation.getId())) {
            this._annotations.push(annotation);
        }
    }

    removeAnnotation(annotationId: string): void {
        this._annotations = this._annotations.filter(a => a.getId() !== annotationId);
    }

    get annotated(): boolean { return this._annotated; }
    setAnnotated(value: boolean) { this._annotated = value; }
}

export const EmptyTerm: Term = Object.create(Term.prototype);

export class Figure {
    private _id: string;
    private _order: number;
    private _annotations: Annotation[];
    private _url: string;
    private _title: string;
    private _s3Key: string = '';
    private _scale: number | undefined;
    private _rotation: number | undefined;
    private _x: number | undefined;
    private _y: number | undefined;
    private _image_with_annotations: string = '';
    private _isAbstract: boolean = false;

    // eslint-disable-next-line complexity
    constructor(params: FigureParams) {
        this._id = params.id;
        this._order = params.order ?? 1;
        this._annotations = [];
        this._url = params.url ?? '';
        this._title = params.title ?? '';
        this._scale = params.scale;
        this._rotation = params.rotation;
        this._x = params.x;
        this._y = params.y;
        this._isAbstract = params.isAbstract ?? false;
    }

    // Getter and Setter methods
    getId(): string { return this._id; }

    get id(): string { return this._id; }

    getOrder(): number { return this._order; }
    setOrder(order: number): void { this._order = order; }

    get annotations(): Annotation[] { return this._annotations; }

    addAnnotation(annotation: Annotation): void {
        if (!this._annotations.some(a => a.getId() === annotation.getId())) {
            this._annotations.push(annotation);
        }
    }

    removeAnnotation(annotationId: string): void {
        this._annotations = this._annotations.filter(a => a.getId() !== annotationId);
    }

    getUrl(): string { return this._url; }
    setUrl(url: string): void { this._url = url; }

    getTitle(): string { return this._title; }
    setTitle(title: string): void { this._title = title; }

    getS3Key(): string { return this._s3Key; }
    setS3Key(s3Key: string): void { this._s3Key = s3Key; }

    get s3Key(): string { return this._s3Key; }

    get scale(): number | undefined { return this._scale; }
    setScale(scale: number): void {
        console.log('set scale', scale);
        this._scale = scale;
    }

    get rotation(): number | undefined { return this._rotation; }
    setRotation(rotation: number): void {
        console.log('set rotation', rotation);
        this._rotation = rotation;
    }

    get x(): number | undefined { return this._x; }
    setX(x: number): void { this._x = x; }

    get y(): number | undefined { return this._y; }
    setY(y: number): void { this._y = y; }

    get imageWithAnnotations() {
        return this._image_with_annotations;
    }

    setImageWithAnnotations(value: string) {
        this._image_with_annotations = value;
    }

    get isAbstract(): boolean {
        return this._isAbstract;
    }

    setAbstract(value: boolean): void {
        this._isAbstract = value;
    }
}

/**
 * 专利说明书
 * @event onFiguresChange 附图变更事件
 * @event onTermsChange 术语变更事件
 * @event onTermRemove 术语移除事件
 * @event onFigureRemove 附图移除事件
 */
@injectable()
export class Specification implements SpecificationFigureService {
    private _figures: Figure[];
    private _terms: Term[];
    private _onFiguresChangeEmitter = new Emitter<Figure[]>();
    /**
     * 附图变更事件
     * @event onFiguresChange
     * @type {Event<Figure[]>}
     * 当附图变更时触发，参数为所有附图对象的数组
     */
    readonly onFiguresChange: Event<Figure[]> = this._onFiguresChangeEmitter.event;
    private _onTermsChangeEmitter = new Emitter<Term[]>();
    /**
     * 术语变更事件
     * @event onTermsChange
     * @type {Event<Term[]>}
     * 当术语变更时触发，参数为所有术语对象的数组
     */
    readonly onTermsChange: Event<Term[]> = this._onTermsChangeEmitter.event;
    private _onTermRemoveEmitter = new Emitter<Term>();
    /**
     * 术语移除事件
     * @event onTermRemove
     * @type {Event<Term>}
     * 当术语被移除时触发，参数为被移除的术语对象
     */
    readonly onTermRemove: Event<Term> = this._onTermRemoveEmitter.event;
    private _onFigureRemoveEmitter = new Emitter<Figure>();
    /**
     * 附图移除事件
     * @event onFigureRemove 附图移除事件
     * @type {Event<Figure>}
     * 当附图被移除时触发，参数为被移除的附图对象
     */
    readonly onFigureRemove: Event<Figure> = this._onFigureRemoveEmitter.event;

    constructor(
        @inject(SpecificationDataService)
        private _specificationDataService: SpecificationDataService,
    ) {
        this._figures = [];
        this._terms = [];
    }

    getAllFigures(): Figure[] { return [...this._figures]; }

    /**
     * 添加单个附图
     * @param figure 附图对象
     * @param emitChange 是否触发变更事件，默认为 true
     */
    addFigure(figure: Figure, emitChange: boolean = true): void {
        if (!figure) return;
        if (!this._figures.some(f => f.getId() === figure.getId())) {
            this._figures.push(figure);
            this._figures.sort((a, b) => a.getOrder() - b.getOrder());
            if (emitChange) {
                this._onFiguresChangeEmitter.fire(this.getAllFigures());
            }
        }
    }

    /**
     * 设置附图集合，会替换现有的所有附图
     * @param figures 附图对象数组
     */
    setFigures(figures: Figure[]): void {
        if (!figures) return;

        // 过滤掉无效的图片并排序
        this._figures = figures
            .filter(figure => figure !== null && figure !== undefined)
            .sort((a, b) => a.getOrder() - b.getOrder());

        // 触发变更事件
        this._onFiguresChangeEmitter.fire(this.getAllFigures());
    }

    removeFigure(figureId: string): void {
        const figure = this._figures.find(f => f.getId() === figureId);
        if (figure) {
            // 移除关联的标注
            figure.annotations.forEach(annotation => {
                const term = this._terms.find(t => t.getId() === annotation.getTermId());
                if (term) {
                    term.removeAnnotation(annotation.getId());
                }
            });
            this._figures = this._figures.filter(f => f.getId() !== figureId);
            this._onFiguresChangeEmitter.fire(this.getAllFigures());
            this._onFigureRemoveEmitter.fire(figure);
        }
    }

    reorderFigures(order: string[]): void {
        const figureMap = new Map(this._figures.map(f => [f.getId(), f]));
        this._figures = order
            .map((id, index) => {
                const figure = figureMap.get(id);
                if (figure) {
                    figure.setOrder(index + 1);
                    return figure;
                }
                return null;
            })
            .filter(f => f !== null) as Figure[];
        this._onFiguresChangeEmitter.fire(this.getAllFigures());
    }

    // 术语相关方法
    getAllTerms(): Term[] { return [...this._terms]; }

    /**
     * 添加术语
     * @param term 术语
     * @param head 是否插入到头部
     */
    addTerm(term: Term, head: boolean = false): void {
        if (!term) return;
        if (!this._terms.some(t => t.getId() === term.getId())) {
            if (head) {
                this._terms.unshift(term);
            } else {
                this._terms.push(term);
            }
            this._onTermsChangeEmitter.fire(this.getAllTerms());
        }
    }

    removeTerm(termId: string): void {
        const term = this._terms.find(t => t.getId() === termId);
        if (term) {
            // 移除关联的标注
            term.annotations.forEach(annotation => {
                const figure = this._figures.find(f =>
                    f.annotations.some(a => a.getId() === annotation.getId()),
                );
                if (figure) {
                    figure.removeAnnotation(annotation.getId());
                }
            });
            this._terms = this._terms.filter(t => t.getId() !== termId);
            this._onTermsChangeEmitter.fire(this.getAllTerms());
            this._onTermRemoveEmitter.fire(term);
        }
    }

    findTermById(termId: string): Term | undefined {
        return this._terms.find(t => t.getId() === termId);
    }

    findTermByNumber(number: string): Term | undefined {
        return this._terms.find(t => t.getNumber() === number);
    }

    clear(): void {
        this._figures = [];
        this._terms = [];
    }

    /**
     * 重新加载附图数据
     * @returns Promise<DataWithLastModified<FigureData[]>> 返回附图数据和最后修改时间
     */
    async reloadFigures() {
        const result = await this._specificationDataService.fetchFigures();
        // 将新的附图数据转换为 Figure 对象并设置
        const figures = result.data.map(figureData => {
            const figure = new Figure({
                id: figureData.id,
                order: figureData.order,
                url: figureData.url,
                title: figureData.title,
                scale: figureData.scale,
                rotation: figureData.rotation,
                x: figureData.x,
                y: figureData.y,
                isAbstract: figureData.isAbstract,
            });
            figure.setS3Key(figureData.s3Key);
            if (figureData.annotationImageBase64) {
                figure.setImageWithAnnotations(figureData.annotationImageBase64);
            }
            return figure;
        });
        this.setFigures(figures);
        return result;
    }
}



export class LocalStorageProvider implements StorageProvider {
    private prefix: string;

    constructor(prefix: string = 'patent_') {
        this.prefix = prefix;
    }

    async save(data: StorageData, path: string): Promise<void> {
        console.log('save', data, path);
    }

    async load(path: string): Promise<StorageData> {
        const dataStr = localStorage.getItem(this.prefix + path);
        if (dataStr === null) {
            throw new Error(`No data found at path: ${path}`);
        }
        return JSON.parse(dataStr) as StorageData;
    }

    async exists(path: string): Promise<boolean> {
        return localStorage.getItem(this.prefix + path) !== null;
    }

    async delete(path: string): Promise<void> {
        localStorage.removeItem(this.prefix + path);
    }
}

export class PatentPersistenceManager {
    private storage: StorageProvider;
    private serializers: PatentDataSerializers;

    constructor(
        storage: StorageProvider,
        serializers: PatentDataSerializers,
    ) {
        this.storage = storage;
        this.serializers = serializers;
    }

    async saveSpecification(spec: Specification, path: string): Promise<void> {
        const now = Date.now();
        const data = {
            terms: spec.getAllTerms().map(term =>
                this.serializers.term.serialize(term),
            ),
            figures: spec.getAllFigures().map(figure => {
                const serializedFigure = this.serializers.figure.serialize(figure);
                serializedFigure.annotations = figure.annotations.map(anno =>
                    this.serializers.annotation.serialize(anno),
                );
                return serializedFigure;
            }),
            lastModified: now,
        };

        await this.storage.save(data, path);
    }

    async loadSpecification(spec: Specification, path: string): Promise<SpecificationData> {
        const data = await this.storage.load(path);

        // 先创建所有术语
        const terms = spec.getAllTerms();

        const figures = data.figures.map((figureData) => {
            const figure = this.serializers.figure.deserialize(figureData);
            for (const annoData of figureData.annotations || []) {
                const term = terms.find((t: Term) => t.getId() === annoData.termId);
                if (!term) continue;

                const annotation = this.serializers.annotation.deserialize({
                    ...annoData,
                    figureId: figure.getId(),
                });
                figure.addAnnotation(annotation);
                term.addAnnotation(annotation);
            }
            return figure;
        });

        return {
            terms,
            figures,
            lastModified: data.lastModified,
        };
    }
}

export class DefaultTermSerializer implements Serializer<Term> {
    serialize(term: Term): any {
        return {
            id: term.getId(),
            text: term.text,
            number: term.getNumber(),
            annotationIds: term.annotations.map(a => a.getId()),
        };
    }

    deserialize(data: any): Term {
        const term = new Term({
            id: data.id,
            text: data.text,
            number: data.number,
        });
        term.setAnnotated(data.annotated);
        return term;
    }
}

export class DefaultFigureSerializer implements Serializer<Figure> {
    serialize(figure: Figure): any {
        return {
            id: figure.getId(),
            order: figure.getOrder(),
            url: figure.getUrl(),
            s3Key: figure.getS3Key(),
            title: figure.getTitle(),
            scale: figure.scale,
            rotation: figure.rotation,
            x: figure.x,
            y: figure.y,
            isAbstract: figure.isAbstract,
            annotationImageBase64: figure.imageWithAnnotations,
            // 注意：annotations将在外层处理
        };
    }

    deserialize(data: any): Figure {
        const figure = new Figure({
            id: data.id,
            order: data.order,
            url: data.url,
            title: data.title,
            scale: data.scale,
            rotation: data.rotation,
            x: data.x,
            y: data.y,
            isAbstract: data.isAbstract,
        });
        figure.setS3Key(data.s3Key);
        figure.setImageWithAnnotations(data.annotationImageBase64);
        return figure;
    }
}

export class RemoteStorageProvider implements StorageProvider {
    private _service: SpecificationDataService;

    constructor(service: SpecificationDataService) {
        this._service = service;
    }

    async save(_data: StorageData, _path: string): Promise<void> {
        throw new Error('RemoteStorageProvider.save 未实现');
    }

    async load(path: string): Promise<StorageData> {
        if (path === 'figures') {
            const { data: figures, lastModified } = await this._service.fetchFigures();

            return {
                figures,
                lastModified,
            };
        }
        throw new Error(`RemoteStorageProvider.load 不支持 path: ${path}`);
    }

    async exists(path: string): Promise<boolean> {
        return true;
    }

    async delete(_path: string): Promise<void> {
        throw new Error('RemoteStorageProvider.delete 未实现');
    }
}
